import { useState, useEffect, useCallback, useRef } from 'react';
import { Heading, Text, Button, Checkbox, Spinner, Dialog, IconButton, Callout } from 'frosted-ui';
import { XMark16, Trophy16, ExclamationTriangle16 } from '@frosted-ui/icons';
import type {
  ExistingPlan,
  Experiment,
  ExperimentMetrics,
  OfferClosedReason,
  PlacementKind,
  PromotionContext,
  Variant,
} from '../../types';
import { api } from '../../services/api';
import {
  describeOffer,
  describePlan,
  formatRpv,
  pluralize,
  variantLabel,
} from '../../utils/variants';
import { boughtLabel, clearLeader, NOT_JUDGED_BY_REVENUE } from '../../utils/readout';
import { formatNumber } from '../../utils/format';

interface PromoteWinnerModalProps {
  experiment: Experiment;
  metrics: ExperimentMetrics | null;
  placements: Array<{ kind: PlacementKind }>;
  // "End test and keep a price": the seller picks before the read is
  // reliable. Same action, with a warning that it's their call.
  early?: boolean;
  // Past its stop date or sales limit: ending doesn't reopen an unlock offer.
  offerClosed?: OfferClosedReason | null;
  onClose: () => void;
  onSuccess: () => void;
}

/** A visible plan identical to `variant`, so keeping it would list the price twice. */
function samePrice(plan: ExistingPlan, variant: Variant): boolean {
  return (
    !plan.billingPeriodDays &&
    !plan.firstCharge &&
    !variant.initialFee &&
    plan.price === variant.price &&
    plan.interval === variant.interval &&
    (plan.trialPeriodDays ?? 0) === (variant.trialDays ?? 0)
  );
}

/** What picking `winner` does to buyers, by where the test is placed. */
export function winnerOutcome(
  winner: string,
  {
    unlock,
    productName,
    placements,
    offerClosed = null,
    offerLimited = false,
  }: {
    unlock: boolean;
    productName: string;
    placements: Array<{ kind: PlacementKind }>;
    offerClosed?: OfferClosedReason | null;
    offerLimited?: boolean; // a stop date or sales limit still to come
  }
): string[] {
  const links = placements.filter((p) => p.kind === 'link' || p.kind === 'partner').length;
  const partners = placements.some((p) => p.kind === 'partner');
  const inWhop = placements.some((p) => p.kind === 'in_whop');
  const lines: string[] = [];
  if (unlock) {
    lines.push(
      offerClosed
        ? `Your offer stays closed: it ${offerClosed === 'sold_out' ? 'has sold out' : 'is past its stop date'}, so buyers can't get ${winner}. This only records your choice.`
        : `Your offer page sells ${winner} to everyone who opens ${links === 1 ? 'the link' : 'any of its links'}${offerLimited ? ', until its stop date or sales limit' : ''}.`
    );
    lines.push('Buyers who already unlocked keep their access.');
  } else {
    lines.push(`${winner} becomes a visible price on ${productName || 'your product'}.`);
    if (links > 0) {
      lines.push(
        links === 1
          ? `Your test link keeps working and sells ${winner}.`
          : `All ${links} test links keep working and sell ${winner}.`
      );
    }
    if (partners) lines.push('Partner links keep their affiliate credit.');
    if (inWhop) {
      lines.push(
        `Free members inside your whop see ${winner} as their upgrade offer, until you run another test on that free product.`
      );
    }
  }
  lines.push('The other prices in this test are archived, and no new visitors are counted.');
  return lines;
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export default function PromoteWinnerModal({
  experiment,
  metrics,
  placements,
  early = false,
  offerClosed = null,
  onClose,
  onSuccess,
}: PromoteWinnerModalProps) {
  const [context, setContext] = useState<PromotionContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariantId, setSelectedVariantId] = useState<string>('');
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [selectedPromoIds, setSelectedPromoIds] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState('');
  const unlock = experiment.offerType === 'unlock';

  const fetchContext = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPromotionContext(experiment._id);
      setContext(data);
      // Preselect the price that's ahead, else Control, never just the first
      // option. Nothing preselected when revenue can't judge the test: it's their call.
      if (metrics && !metrics.revenueComparable) return;
      const leader = metrics ? clearLeader(metrics.variants) : null;
      const preselect =
        data.variants.find((v) => v._id === leader?.variantId) ??
        data.variants.find((v) => v.name === 'A') ??
        data.variants[0];
      if (preselect) setSelectedVariantId(preselect._id);
    } catch (err) {
      setError('Couldn’t load this test. Try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [experiment._id, metrics]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  async function handlePromote() {
    if (!selectedVariantId) return;
    try {
      setPromoting(true);
      setError('');
      await api.promoteWinner(experiment._id, selectedVariantId, selectedPlanIds, selectedPromoIds);
      onSuccess();
    } catch (err) {
      setError((err as Error).message || 'Couldn’t end the test. Try again.');
      console.error(err);
    } finally {
      setPromoting(false);
    }
  }

  function toggle(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  const metricsFor = (variant: Variant) =>
    metrics?.variants.find((m) => m.variantId === variant._id) ?? null;
  const currency = experiment.variants?.[0]?.currency ?? context?.variants[0]?.currency ?? 'usd';
  const selectedVariant = context?.variants.find((v) => v._id === selectedVariantId);
  const selectedName = selectedVariant ? variantLabel(selectedVariant.name) : 'the winner';
  // An unlock test's product is its own hidden one: nothing else to hide.
  const otherPlans = unlock
    ? []
    : (context?.existingVisiblePlans.filter((p) => !p.isExperimentPlan) ?? []);
  const variantsWithPromos = (context?.variants ?? []).filter(
    (v): v is Variant & { whopPromoCodeId: string; whopPromoCode: string } =>
      Boolean(v.whopPromoCodeId && v.whopPromoCode)
  );
  const judgedByRevenue = metrics?.revenueComparable ?? true;
  const leaderId = metrics && judgedByRevenue ? clearLeader(metrics.variants)?.variantId : undefined;
  // A visible price identical to the one being kept: ticked to come off the
  // page, so the store doesn't list it twice. The seller can untick it.
  const twinIds = selectedVariant
    ? otherPlans.filter((p) => samePrice(p, selectedVariant)).map((p) => p.id)
    : [];
  const twinKey = twinIds.join(',');
  const tickedTwins = useRef<string[]>([]);
  useEffect(() => {
    const twins = twinKey ? twinKey.split(',') : [];
    const previous = tickedTwins.current;
    tickedTwins.current = twins;
    setSelectedPlanIds((ids) => [...new Set([...ids.filter((id) => !previous.includes(id)), ...twins])]);
  }, [twinKey]);
  const startedWord = boughtLabel(experiment.offerType, experiment.variants ?? []);
  const hasVisitors = (metrics?.totalVisits ?? 0) > 0;
  const outcome = winnerOutcome(selectedName, {
    unlock,
    productName: experiment.productName,
    placements,
    offerClosed,
    offerLimited: Boolean(experiment.unlock?.expiresAt || experiment.unlock?.maxSales),
  });
  const title = early ? 'End test and keep a price' : 'Pick a winner';

  return (
    <Dialog.Root open onOpenChange={(open: boolean) => !open && onClose()}>
      <Dialog.Content
        aria-describedby={undefined}
        style={{ maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            paddingBottom: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            borderBottom: '1px solid var(--gray-a6)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Trophy16 style={{ color: 'var(--accent-9)' }} />
            <Dialog.Title size="5" style={{ margin: 0 }}>
              {title}
            </Dialog.Title>
          </div>
          <IconButton
            variant="ghost"
            color="gray"
            size="3"
            aria-label="Close"
            onClick={onClose}
            className="pl-tap"
          >
            <XMark16 />
          </IconButton>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
            <Spinner size="3" />
          </div>
        ) : error && !context ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
            <Text color="red" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
              {error}
            </Text>
            <Button variant="soft" color="gray" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : !confirming ? (
          <>
            {early && (
              <Callout.Root color="yellow" style={{ marginBottom: 'var(--space-4)' }}>
                <Callout.Icon>
                  <ExclamationTriangle16 />
                </Callout.Icon>
                <Callout.Text>
                  {judgedByRevenue ? (
                    <>
                      Results aren’t reliable yet — you’re choosing, not the data.
                      {metrics && hasVisitors
                        ? ` ${pluralize(metrics.totalVisits, 'visitor')} so far; a result needs ${formatNumber(metrics.minVisitorsPerVariant)} visitors and ${formatNumber(metrics.minConversionsPerVariant)} ${unlock ? 'unlocks' : 'sales'} on each price.`
                        : ''}
                    </>
                  ) : (
                    NOT_JUDGED_BY_REVENUE
                  )}{' '}
                  Ending the test can’t be undone.
                </Callout.Text>
              </Callout.Root>
            )}

            <Heading size="3" id="keep-price-heading" style={{ marginBottom: 'var(--space-1)' }}>
              Which price do you want to keep?
            </Heading>
            <Text
              size="2"
              color="gray"
              style={{ display: 'block', marginBottom: 'var(--space-3)' }}
            >
              {!hasVisitors
                ? 'No visitors yet, so there’s nothing to compare.'
                : judgedByRevenue
                  ? leaderId
                    ? 'Preselected: the highest revenue per visitor so far.'
                    : 'No price is ahead, so Control is preselected.'
                  : 'Nothing is preselected. Here’s what each price has so far.'}
            </Text>
            <div
              role="group"
              aria-labelledby="keep-price-heading"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-5)',
              }}
            >
              {context?.variants.map((variant) => {
                const m = metricsFor(variant);
                const selected = selectedVariantId === variant._id;
                const chance = m?.isControl
                  ? 'Compared against'
                  : m?.probabilityToBeatControl != null
                    ? `${percent(m.probabilityToBeatControl)} chance it beats Control`
                    : 'Chance it beats Control: —';
                return (
                  <button
                    key={variant._id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedVariantId(variant._id)}
                    className="vara2-card-hover"
                    style={{
                      width: '100%',
                      minHeight: 44,
                      padding: 'var(--space-3) var(--space-4)',
                      borderRadius: 'var(--radius-3)',
                      border: `2px solid ${selected ? 'var(--accent-9)' : 'var(--gray-a6)'}`,
                      backgroundColor: selected ? 'var(--accent-a3)' : 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: 'inherit',
                      font: 'inherit',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        gap: 'var(--space-1) var(--space-3)',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <Text weight="medium" style={{ display: 'block' }}>
                          {variantLabel(variant.name)}
                          {hasVisitors && variant._id === leaderId && (
                            <Text size="1" color="gray" weight="regular">
                              {' '}
                              · ahead so far
                            </Text>
                          )}
                        </Text>
                        <Text size="2" color="gray">
                          {describeOffer(variant)}
                        </Text>
                      </div>
                      {/* Grows to the full row once it wraps under the name on
                          phones, so both lines stay flush right. */}
                      <div style={{ flexGrow: 1, textAlign: 'right', minWidth: 0 }}>
                        <Text
                          size="2"
                          weight="medium"
                          className="vara2-tabular"
                          style={{ display: 'block' }}
                        >
                          {!m
                            ? '—'
                            : judgedByRevenue
                              ? m.visits === 0
                                ? 'No visitors yet'
                                : `${formatRpv(m.revenuePerVisitor, currency)} per visitor`
                              : pluralize(m.visits, 'visitor')}
                        </Text>
                        <Text size="1" color="gray" className="vara2-tabular">
                          {judgedByRevenue ? chance : `${startedWord}: ${m?.starters ?? 0}`}
                        </Text>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {otherPlans.length > 0 && (
              <div style={{ marginBottom: 'var(--space-5)' }}>
                <Heading size="3" style={{ marginBottom: 'var(--space-1)' }}>
                  Hide your other prices?
                </Heading>
                <Text
                  size="2"
                  color="gray"
                  style={{ display: 'block', marginBottom: 'var(--space-3)' }}
                >
                  These prices are on your {experiment.productName || 'product'} store page today.
                  Tick any to take off the page, so new buyers only see {selectedName}. Anyone with
                  a direct link to one can still buy it.
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {otherPlans.map((plan) => (
                    <label
                      key={plan.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        minHeight: 44,
                        padding: 'var(--space-2) var(--space-3)',
                        borderRadius: 'var(--radius-2)',
                        border: '1px solid var(--gray-a6)',
                        cursor: 'pointer',
                      }}
                    >
                      <Checkbox
                        size="2"
                        checked={selectedPlanIds.includes(plan.id)}
                        onCheckedChange={() => setSelectedPlanIds((prev) => toggle(prev, plan.id))}
                      />
                      <div style={{ minWidth: 0 }}>
                        <Text size="2" weight="medium" style={{ display: 'block' }}>
                          {describePlan({ ...plan, currency })}
                        </Text>
                        {twinIds.includes(plan.id) && (
                          <Text size="1" color="gray" style={{ display: 'block' }}>
                            The same as {selectedName}. Ticked so your store page doesn't show this
                            price twice; untick it to keep both.
                          </Text>
                        )}
                        {(plan.fromTest || plan.name) && (
                          <Text size="1" color="gray" style={{ display: 'block' }}>
                            {plan.fromTest
                              ? `From your test “${plan.fromTest.name}”. Its links keep selling this price either way.`
                              : plan.name}
                          </Text>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {variantsWithPromos.length > 0 && (
              <div style={{ marginBottom: 'var(--space-5)' }}>
                <Heading size="3" style={{ marginBottom: 'var(--space-1)' }}>
                  Turn off this test’s promo codes?
                </Heading>
                <Text
                  size="2"
                  color="gray"
                  style={{ display: 'block', marginBottom: 'var(--space-3)' }}
                >
                  A turned-off code can’t be used again, and Whop can’t turn it back on. Leave it if
                  you use the code anywhere else.
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {variantsWithPromos.map((variant) => (
                    <label
                      key={variant._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        minHeight: 44,
                        padding: 'var(--space-2) var(--space-3)',
                        borderRadius: 'var(--radius-2)',
                        border: '1px solid var(--gray-a6)',
                        cursor: 'pointer',
                      }}
                    >
                      <Checkbox
                        size="2"
                        checked={selectedPromoIds.includes(variant.whopPromoCodeId)}
                        onCheckedChange={() =>
                          setSelectedPromoIds((prev) => toggle(prev, variant.whopPromoCodeId))
                        }
                      />
                      <Text size="2">
                        <Text
                          weight="medium"
                          style={{ fontFamily: 'var(--code-font-family, monospace)' }}
                        >
                          {variant.whopPromoCode}
                        </Text>{' '}
                        <Text color="gray">on {variantLabel(variant.name)}</Text>
                      </Text>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <Button
              variant="solid"
              size="3"
              onClick={() => setConfirming(true)}
              disabled={!selectedVariantId}
              style={{ width: '100%' }}
            >
              Continue
            </Button>
          </>
        ) : (
          <div className="vara2-step-in">
            <div
              style={{
                backgroundColor: 'var(--gray-a3)',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-3)',
                marginBottom: 'var(--space-4)',
              }}
            >
              <Text size="1" color="gray" style={{ display: 'block' }}>
                {early ? 'You’re keeping' : 'Winner'}
              </Text>
              <Heading size="4">
                {selectedName}
                {selectedVariant ? ` · ${describeOffer(selectedVariant)}` : ''}
              </Heading>
            </div>

            <Heading size="3" style={{ marginBottom: 'var(--space-2)' }}>
              What happens
            </Heading>
            <ul
              style={{
                margin: '0 0 var(--space-3) var(--space-4)',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-1)',
              }}
            >
              {outcome.map((line) => (
                <li key={line}>
                  <Text size="2">{line}</Text>
                </li>
              ))}
              {selectedPlanIds.length > 0 && (
                <li>
                  <Text size="2">
                    {pluralize(selectedPlanIds.length, 'other price')}{' '}
                    {selectedPlanIds.length === 1 ? 'comes' : 'come'} off your store page. Direct
                    links to {selectedPlanIds.length === 1 ? 'it' : 'them'} keep working.
                  </Text>
                </li>
              )}
              {selectedPromoIds.length > 0 && (
                <li>
                  <Text size="2">
                    {pluralize(selectedPromoIds.length, 'promo code')}{' '}
                    {selectedPromoIds.length === 1 ? 'is' : 'are'} turned off for good.
                  </Text>
                </li>
              )}
            </ul>
            <Text
              size="2"
              weight="medium"
              style={{ display: 'block', marginBottom: 'var(--space-4)' }}
            >
              {early
                ? 'This ends the test for good, on your call rather than a result.'
                : 'This ends the test and can’t be undone.'}
            </Text>

            {error && (
              <Text
                size="2"
                color="red"
                style={{ display: 'block', marginBottom: 'var(--space-4)' }}
              >
                {error}
              </Text>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
              <Button
                variant="soft"
                color="gray"
                size="3"
                onClick={() => setConfirming(false)}
                disabled={promoting}
                style={{ flex: '1 1 140px' }}
              >
                Back
              </Button>
              <Button
                variant="solid"
                color={early ? undefined : 'green'}
                size="3"
                onClick={handlePromote}
                disabled={promoting}
                loading={promoting}
                style={{ flex: '1 1 200px' }}
              >
                <Trophy16 />
                {early ? `End test, keep ${selectedName}` : `Pick ${selectedName}`}
              </Button>
            </div>
          </div>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
